<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('regexTester.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('regexTester.title') }}</h1>
      <p>{{ $t('regexTester.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="regex-input-row">
        <span class="regex-delim">/</span>
        <input
          v-model="pattern"
          class="regex-pattern"
          :placeholder="$t('regexTester.patternPlaceholder')"
          spellcheck="false"
          @input="testRegex"
        />
        <span class="regex-delim">/</span>
        <input
          v-model="flags"
          class="regex-flags"
          placeholder="gi"
          spellcheck="false"
          @input="testRegex"
        />
      </div>

      <div v-if="regexError" class="alert error">{{ regexError }}</div>

      <div class="editor-container">
        <div class="editor-panel">
          <label>{{ $t('regexTester.testString') }}</label>
          <textarea
            v-model="testStr"
            :placeholder="$t('regexTester.testPlaceholder')"
            spellcheck="false"
            @input="testRegex"
          ></textarea>
        </div>
        <div class="editor-panel">
          <label>{{ $t('regexTester.matchResult') }}
            <span v-if="matchCount >= 0" class="stats-info">{{ $t('regexTester.matchCount').replace('{n}', matchCount) }}</span>
          </label>
          <div class="result-area" v-html="highlightedText"></div>
        </div>
      </div>

      <div v-if="matches.length > 0" class="matches-section">
        <label>{{ $t('regexTester.matchDetails') }}</label>
        <div class="matches-table">
          <div class="match-row header">
            <span class="match-index">#</span>
            <span class="match-value">{{ $t('regexTester.matchValue') }}</span>
            <span class="match-pos">{{ $t('regexTester.matchPos') }}</span>
            <span class="match-groups">{{ $t('regexTester.matchGroups') }}</span>
          </div>
          <div class="match-row" v-for="(m, i) in matches" :key="i">
            <span class="match-index">{{ i + 1 }}</span>
            <span class="match-value"><code>{{ m.value }}</code></span>
            <span class="match-pos">{{ m.index }}-{{ m.index + m.value.length }}</span>
            <span class="match-groups">{{ m.groups || '-' }}</span>
          </div>
        </div>
      </div>

      <div class="quick-ref">
        <label>{{ $t('regexTester.quickRef') }}</label>
        <div class="ref-grid">
          <div class="ref-item" v-for="item in quickRefItems" :key="item.pattern" @click="insertPattern(item.pattern)">
            <code>{{ item.pattern }}</code>
            <span>{{ item.desc }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="tool-description">
      <h2>{{ $t('regexTester.whatIs') }}</h2>
      <p>{{ $t('regexTester.whatIsDesc') }}</p>
      <h2>{{ $t('regexTester.useCasesTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('regexTester.useCases')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RegexTester',
  data() {
    return {
      pattern: '',
      flags: 'g',
      testStr: '',
      regexError: '',
      matchCount: -1,
      matches: [],
      highlightedText: ''
    }
  },
  computed: {
    quickRefItems() {
      return [
        { pattern: '.', desc: this.$t('regexTester.ref.dot') },
        { pattern: '\\d', desc: this.$t('regexTester.ref.digit') },
        { pattern: '\\w', desc: this.$t('regexTester.ref.word') },
        { pattern: '\\s', desc: this.$t('regexTester.ref.space') },
        { pattern: '^', desc: this.$t('regexTester.ref.start') },
        { pattern: '$', desc: this.$t('regexTester.ref.end') },
        { pattern: '*', desc: this.$t('regexTester.ref.star') },
        { pattern: '+', desc: this.$t('regexTester.ref.plus') },
        { pattern: '?', desc: this.$t('regexTester.ref.question') },
        { pattern: '[abc]', desc: this.$t('regexTester.ref.charset') },
        { pattern: '(abc)', desc: this.$t('regexTester.ref.group') },
        { pattern: 'a|b', desc: this.$t('regexTester.ref.or') }
      ]
    }
  },
  methods: {
    testRegex() {
      this.regexError = ''
      this.matches = []
      this.matchCount = -1
      this.highlightedText = this.escapeHtml(this.testStr)

      if (!this.pattern || !this.testStr) return

      let regex
      try {
        regex = new RegExp(this.pattern, this.flags)
      } catch (e) {
        this.regexError = e.message
        return
      }

      const allMatches = []
      let match
      if (this.flags.includes('g')) {
        while ((match = regex.exec(this.testStr)) !== null) {
          allMatches.push({
            value: match[0],
            index: match.index,
            groups: match.slice(1).length > 0 ? match.slice(1).join(', ') : null
          })
          if (match[0].length === 0) regex.lastIndex++
          if (allMatches.length > 500) break
        }
      } else {
        match = regex.exec(this.testStr)
        if (match) {
          allMatches.push({
            value: match[0],
            index: match.index,
            groups: match.slice(1).length > 0 ? match.slice(1).join(', ') : null
          })
        }
      }

      this.matches = allMatches
      this.matchCount = allMatches.length
      this.buildHighlight(allMatches)
    },
    buildHighlight(allMatches) {
      if (allMatches.length === 0) {
        this.highlightedText = this.escapeHtml(this.testStr)
        return
      }
      let result = ''
      let lastIndex = 0
      for (const m of allMatches) {
        result += this.escapeHtml(this.testStr.slice(lastIndex, m.index))
        result += '<mark>' + this.escapeHtml(m.value) + '</mark>'
        lastIndex = m.index + m.value.length
      }
      result += this.escapeHtml(this.testStr.slice(lastIndex))
      this.highlightedText = result
    },
    escapeHtml(str) {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/\n/g, '<br>')
    },
    insertPattern(p) {
      this.pattern += p
      this.testRegex()
    }
  }
}
</script>

<style scoped>
.tool-page { max-width: 1200px; margin: 0 auto; }
.breadcrumb { margin-bottom: 24px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb .separator { margin: 0 8px; color: var(--text-muted); }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 32px; margin-bottom: 12px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 16px; max-width: 600px; margin: 0 auto; line-height: 1.6; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; box-shadow: var(--shadow-md); }
.regex-input-row { display: flex; align-items: center; gap: 4px; margin-bottom: 16px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 8px 12px; }
.regex-delim { font-size: 20px; color: var(--accent-color); font-weight: bold; font-family: monospace; }
.regex-pattern { flex: 1; background: none; border: none; color: var(--text-primary); font-family: 'Monaco','Menlo','Ubuntu Mono',monospace; font-size: 16px; outline: none; }
.regex-flags { width: 50px; background: none; border: none; border-left: 1px solid var(--border-color); padding-left: 8px; color: var(--accent-color); font-family: 'Monaco','Menlo','Ubuntu Mono',monospace; font-size: 16px; outline: none; }
.alert.error { padding: 10px 16px; border-radius: 8px; margin-bottom: 16px; background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); color: #ef4444; font-size: 13px; }
.editor-container { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
@media (max-width: 768px) { .editor-container { grid-template-columns: 1fr; } }
.editor-panel label { font-size: 14px; color: var(--text-secondary); margin-bottom: 8px; display: flex; align-items: center; gap: 8px; }
.stats-info { font-size: 12px; color: var(--accent-color); }
.editor-panel textarea { min-height: 200px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 16px; color: var(--text-primary); font-family: 'Monaco','Menlo','Ubuntu Mono',monospace; font-size: 13px; line-height: 1.6; resize: vertical; width: 100%; box-sizing: border-box; }
.editor-panel textarea:focus { outline: none; border-color: var(--accent-color); }
.result-area { min-height: 200px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 16px; font-family: 'Monaco','Menlo','Ubuntu Mono',monospace; font-size: 13px; line-height: 1.6; color: var(--text-primary); overflow: auto; white-space: pre-wrap; word-break: break-all; }
.result-area :deep(mark) { background: rgba(251,191,36,0.4); color: inherit; border-radius: 2px; padding: 1px 2px; }
.matches-section { margin-bottom: 20px; }
.matches-section > label { font-size: 14px; color: var(--text-secondary); margin-bottom: 8px; display: block; }
.matches-table { background: var(--bg-tertiary); border-radius: 8px; overflow: hidden; border: 1px solid var(--border-color); }
.match-row { display: grid; grid-template-columns: 40px 1fr 100px 1fr; gap: 12px; padding: 8px 16px; align-items: center; font-size: 13px; }
.match-row.header { background: var(--bg-hover); font-weight: 600; color: var(--text-secondary); }
.match-row:not(.header) { border-top: 1px solid var(--border-color); }
.match-row code { background: rgba(251,191,36,0.15); padding: 2px 6px; border-radius: 4px; font-family: monospace; }
.match-pos { color: var(--text-secondary); font-family: monospace; }
.match-groups { color: var(--text-secondary); font-size: 12px; }
.quick-ref > label { font-size: 14px; color: var(--text-secondary); margin-bottom: 10px; display: block; font-weight: 600; }
.ref-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 8px; }
.ref-item { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: var(--bg-tertiary); border-radius: 6px; cursor: pointer; transition: background 0.2s; font-size: 13px; }
.ref-item:hover { background: var(--bg-hover); }
.ref-item code { background: var(--accent-light); padding: 2px 8px; border-radius: 4px; font-family: monospace; font-weight: 600; color: var(--accent-color); min-width: 44px; text-align: center; }
.ref-item span { color: var(--text-secondary); }
.tool-description { margin-top: 32px; background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; box-shadow: var(--shadow-sm); }
.tool-description h2 { font-size: 20px; color: var(--text-primary); margin: 24px 0 16px; padding-bottom: 8px; border-bottom: 2px solid var(--accent-light); }
.tool-description h2:first-child { margin-top: 0; }
.tool-description p { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 16px; }
.tool-description ul { padding-left: 24px; margin-bottom: 16px; }
.tool-description li { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 8px; }
</style>
